import { Button } from '@progress/kendo-react-buttons';
import { SvgIcon } from '@progress/kendo-react-common';
import { TextArea } from '@progress/kendo-react-inputs';
import React from 'react';
import { ArrowDownOutline, CloseOutline, RefreshOutline, SendSolid, SparkSolid, WarningCircleOutline } from '../../../icons/icons';
import { EventBus, useEvent } from '../../contexts/event-bus';
import { useAppDispatch } from '../../hooks/store-hooks';
import { aiChatActions } from '../../state/slices/ai-chat-slice';
import { AiMessage } from './AiMessage';
import { AI_PROMPT_MAX_LENGTH } from './ai-chat-helpers';
import './ai-chat.css';
import { useAiChat } from './use-ai-chat';

const ENABLE_ASK_AI_RAINBOW_ANIMATION = true;
const CLOSE_ANIMATION_MS = 150;
const STICK_TO_BOTTOM_THRESHOLD_PX = 40;
const COUNTER_THRESHOLD = Math.floor(AI_PROMPT_MAX_LENGTH * 0.8);
const EXAMPLE_PROMPTS = [
    'Flights from KLAX to KJFK above FL300',
    'Show diverted flights',
    'Show flights that are holding',
    'Remove all filters from the view'
];

function RainbowPill(props: { rainbow: boolean; className?: string; children: React.ReactNode }) {
    const rainbow = ENABLE_ASK_AI_RAINBOW_ANIMATION && props.rainbow ? 'ai-chat-rainbow' : '';
    return <div className={`ai-chat-pill-wrap ${rainbow} ${props.className ?? ''}`}>{props.children}</div>;
}

export function AiChatToolbarButton() {
    return (
        <RainbowPill rainbow className="ai-chat-toolbar-wrap">
            <button
                type="button"
                className="ai-chat-pill-btn ai-chat-toolbar-btn"
                data-tooltip-bottom
                title="Ask AI – filters assistant"
                onClick={() => EventBus.fire('toggle-ai-chat', null)}>
                <SvgIcon icon={SparkSolid} size="small" />
                <span>Ask AI</span>
            </button>
        </RainbowPill>
    );
}

function AiEmptyState(props: { onPick: (text: string) => void }) {
    return (
        <div className="ai-chat-empty d-flex flex-column align-items-center text-center gap-2">
            <span className="ai-chat-empty-icon">
                <SvgIcon icon={SparkSolid} size="large" />
            </span>
            <div className="k-font-weight-medium">What do you want to filter?</div>
            <div className="secondary-text-color">Describe it in plain language and I'll create, change or remove filters for you.</div>
            <div className="d-flex flex-wrap justify-content-center gap-1 mt-1">
                {EXAMPLE_PROMPTS.map((example) => (
                    <button key={example} type="button" className="ai-chat-chip" onClick={() => props.onPick(example)}>
                        {example}
                    </button>
                ))}
            </div>
        </div>
    );
}

function TypingIndicator() {
    return (
        <div className="ai-chat-message ai-chat-message-assistant" role="status" aria-label="AI is working on your request">
            <span className="ai-chat-avatar ai-chat-avatar-busy" aria-hidden="true">
                <SvgIcon icon={SparkSolid} size="small" />
            </span>
            <div className="ai-chat-typing d-flex align-items-center gap-1">
                <span />
                <span />
                <span />
            </div>
        </div>
    );
}

export function AiChatWindow() {
    const dispatch = useAppDispatch();
    const { chat, filters, send, retry, revert } = useAiChat();
    const [open, setOpen] = React.useState(false);
    const [closing, setClosing] = React.useState(false);
    const [prompt, setPrompt] = React.useState('');
    const [atBottom, setAtBottom] = React.useState(true);
    const { messages, loading, error } = chat;
    const askEnabled = prompt.trim().length > 0 && !loading;
    const responseRef = React.useRef<HTMLDivElement>(null);
    const stickToBottom = React.useRef(true);
    const closeTimer = React.useRef<number | undefined>(undefined);

    React.useEffect(() => () => window.clearTimeout(closeTimer.current), []);

    const close = React.useCallback(() => {
        if (closeTimer.current) {
            return;
        }
        setClosing(true);
        closeTimer.current = window.setTimeout(() => {
            closeTimer.current = undefined;
            setClosing(false);
            setOpen(false);
        }, CLOSE_ANIMATION_MS);
    }, []);

    useEvent(
        'toggle-ai-chat',
        React.useCallback(() => {
            if (!open) {
                setOpen(true);
            } else if (!closing) {
                close();
            }
        }, [open, closing, close])
    );

    const scrollToBottom = React.useCallback((smooth = false) => {
        const el = responseRef.current;
        if (el) {
            stickToBottom.current = true;
            el.scrollTo({ top: el.scrollHeight, behavior: smooth ? 'smooth' : 'auto' });
        }
    }, []);

    React.useEffect(() => {
        if (!open) {
            stickToBottom.current = true;
            return;
        }
        if (stickToBottom.current) {
            scrollToBottom();
        }
    }, [messages.length, loading, error, open, scrollToBottom]);

    React.useEffect(() => {
        if (messages.length === 0) {
            setPrompt('');
            setAtBottom(true);
        }
    }, [messages.length]);

    const onScroll = () => {
        const el = responseRef.current;
        if (el) {
            const near = el.scrollHeight - el.scrollTop - el.clientHeight < STICK_TO_BOTTOM_THRESHOLD_PX;
            stickToBottom.current = near;
            setAtBottom(near);
        }
    };

    const ask = React.useCallback(
        (text: string) => {
            if (text.trim() && !loading) {
                setPrompt('');
                stickToBottom.current = true;
                send(text);
            }
        },
        [loading, send]
    );

    const onKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            ask(prompt);
        } else if (e.key === 'Escape') {
            e.preventDefault();
            close();
        }
    };

    if (!open) {
        return null;
    }

    const lastMessage = messages[messages.length - 1];
    const canRetry = !!error && lastMessage?.role === 'user' && !loading;
    const windowClasses = [
        'ai-chat-window',
        closing ? 'ai-chat-window-closing' : '',
        ENABLE_ASK_AI_RAINBOW_ANIMATION && loading ? 'ai-chat-window-loading' : ''
    ].join(' ');

    return (
        <div className={windowClasses} role="dialog" aria-label="Ask AI">
            <div className="ai-chat-window-inner d-flex flex-column">
                <div className="ai-chat-header d-flex align-items-center justify-content-between ps-3 pe-1">
                    <div className="d-flex align-items-center gap-2">
                        <SvgIcon icon={SparkSolid} className={loading ? 'ai-chat-pulse' : ''} />
                        <div className="d-flex flex-column ai-chat-header-text">
                            <span className="k-font-weight-medium d-flex align-items-center gap-1">
                                Ask AI
                                <span className="fe-badge-secondary ai-chat-beta">Beta</span>
                            </span>
                            <span className="secondary-text-color ai-chat-subtitle">Filters assistant</span>
                        </div>
                    </div>
                    <div className="d-flex">
                        <Button
                            fillMode="flat"
                            svgIcon={RefreshOutline}
                            title="New chat"
                            aria-label="New chat"
                            disabled={loading || messages.length === 0}
                            onClick={() => dispatch(aiChatActions.clear())}
                        />
                        <Button fillMode="flat" svgIcon={CloseOutline} title="Close (Esc)" aria-label="Close" onClick={close} />
                    </div>
                </div>

                <div className="ai-chat-body d-flex flex-column">
                    <div
                        ref={responseRef}
                        className="ai-chat-response px-3 flight-details-scrollbars"
                        role="log"
                        aria-live="polite"
                        onScroll={onScroll}>
                        {messages.length === 0 && !loading && <AiEmptyState onPick={ask} />}
                        {messages.map((m, i) => (
                            <AiMessage
                                key={m.id}
                                message={m}
                                next={messages[i + 1]}
                                isLast={m === lastMessage}
                                loading={loading}
                                filters={filters}
                                onAsk={ask}
                                onRevert={revert}
                            />
                        ))}
                        {loading && <TypingIndicator />}
                        {error && (
                            <div className="ai-chat-error d-flex align-items-center gap-2" role="alert">
                                <SvgIcon icon={WarningCircleOutline} />
                                <span className="flex-grow-1">{error}</span>
                                {canRetry && (
                                    <Button fillMode="outline" className="button-small" svgIcon={RefreshOutline} onClick={retry}>
                                        Retry
                                    </Button>
                                )}
                            </div>
                        )}
                    </div>
                    {!atBottom && messages.length > 0 && (
                        <button type="button" className="ai-chat-jump" onClick={() => scrollToBottom(true)}>
                            <SvgIcon icon={ArrowDownOutline} size="small" />
                            Latest
                        </button>
                    )}
                </div>

                <div className="ai-chat-input-area">
                    <div className="ai-chat-input-box d-flex align-items-center gap-2">
                        <TextArea
                            key={messages.length}
                            autoFocus
                            className="ai-chat-input"
                            data-allow-shortcuts="true"
                            placeholder="Describe aircraft filters..."
                            value={prompt}
                            rows={1}
                            autoSize
                            resizable="none"
                            maxLength={AI_PROMPT_MAX_LENGTH}
                            disabled={loading}
                            onChange={(e) => setPrompt(String(e.value ?? ''))}
                            onKeyDown={onKeyDown}
                        />
                        <RainbowPill rainbow={askEnabled} className="ai-chat-rainbow-small">
                            <button
                                type="button"
                                className="ai-chat-pill-btn ai-chat-send-btn"
                                disabled={!askEnabled}
                                onClick={() => ask(prompt)}
                                aria-label="Send"
                                title="Send (Enter)">
                                <SvgIcon icon={SendSolid} />
                            </button>
                        </RainbowPill>
                    </div>
                    <div className="ai-chat-input-footer d-flex justify-content-between secondary-text-color">
                        <span>Enter to send · Shift+Enter for a new line</span>
                        {prompt.length >= COUNTER_THRESHOLD && (
                            <span className={prompt.length >= AI_PROMPT_MAX_LENGTH ? 'ai-chat-counter-max' : ''}>
                                {prompt.length}/{AI_PROMPT_MAX_LENGTH}
                            </span>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
